import { useUser } from "@/redux/user";
import { useEffect, useState } from "react";
import useTranslation from "next-translate/useTranslation";
import styles from "./styles/Settings.module.css";
import Display from "../../components/ui/display";
import Toggle from "../../components/ui/toggle";
import { api } from "../../services/api";

type Preferences = {
  newsletter: boolean;
  gameAnalysis: boolean;
};

const defaultPreferences: Preferences = {
  newsletter: false,
  gameAnalysis: true,
};

export default function EmailPreferences() {
  const { t } = useTranslation("auth");
  const { email, emailNotActivated, hasRecoveryCode } = useUser();

  const [prefs, setPrefs] = useState<Preferences>(defaultPreferences);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<keyof Preferences | null>(null);

  useEffect(() => {
    let isMounted = true;

    api
      .get("/users/email-preferences")
      .then((response) => {
        if (!isMounted) {
          return;
        }

        setPrefs({
          newsletter: !!response.data?.newsletter,
          gameAnalysis: response.data?.gameAnalysis !== false,
        });
      })
      .catch((error) => {
        console.error("Failed to load email preferences:", error);
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  function updatePreference(name: keyof Preferences, value: boolean) {
    const previous = prefs[name];
    setPrefs((prev) => ({ ...prev, [name]: value }));
    setSaving(name);

    api
      .post("/users/email-preferences", { [name]: value })
      .catch((error) => {
        console.error("Failed to update email preference:", error);
        setPrefs((prev) => ({ ...prev, [name]: previous }));
      })
      .finally(() => {
        setSaving(null);
      });
  }

  if (hasRecoveryCode && (!email || emailNotActivated))
    return (
      <div className={styles.body}>
        <div className={styles.title}>{t("Email settings")}</div>
        <p className="mt-4">{t("emailNeededForPreferences")}</p>
      </div>
    );

  return (
    <div className={styles.body}>
      <div className={styles.title}>{t("Email settings")}</div>
      <div className={styles.items}>
        <Display title={t("Newsletter and promotions")}>
          <Toggle
            active={prefs.newsletter}
            onToggle={(isOn) => updatePreference("newsletter", isOn)}
          />
        </Display>
        <Display title={t("Learning emails")}>
          <Toggle
            active={prefs.gameAnalysis}
            onToggle={(isOn) => updatePreference("gameAnalysis", isOn)}
          />
        </Display>
        <p className="text-sm text-altgray-500 mt-2">
          {t("Learning email frequency")}
        </p>
      </div>
      {loading && (
        <div className="text-sm text-altgray-500 mt-2">{t("Loading...")}</div>
      )}
      {!loading && saving && (
        <div className="text-sm text-altgray-500 mt-2">{t("Saving...")}</div>
      )}
    </div>
  );
}
